import React from 'react';
import { ListOrdered, Trash2, X } from 'lucide-react';
import { EnhancedSearch } from './EnhancedSearch';

const normalizeCaseId = (item) => {
    const rawId = item?.value ?? item?.id ?? item;
    return rawId === null || rawId === undefined ? '' : String(rawId);
};

const buildCaseSelection = (result) => ({
    id: normalizeCaseId(result),
    label: result?.label || `Case ${normalizeCaseId(result)}`,
    phase: result?.phase || '',
    contactName: result?.contact_name || (result?.contactName !== result?.label ? result?.contactName : '') || ''
});

const updateListCriteria = (criteria, selectedCases) => {
    const orderedCaseIds = selectedCases.map(normalizeCaseId).filter(Boolean);

    return {
        ...criteria,
        logic: 'AND',
        report_mode: 'list',
        ordered_case_ids: orderedCaseIds,
        selected_cases: selectedCases,
        conditions: [{
            field_id: 'cases.id',
            operator: '=',
            value: orderedCaseIds,
            _list_report: true
        }]
    };
};

export const ListReportCases = ({ selectedCriteria, setSelectedCriteria }) => {
    const orderedCaseIds = Array.isArray(selectedCriteria?.ordered_case_ids)
        ? selectedCriteria.ordered_case_ids.map(normalizeCaseId).filter(Boolean)
        : [];
    const savedCases = Array.isArray(selectedCriteria?.selected_cases)
        ? selectedCriteria.selected_cases
        : [];
    const savedCasesById = new Map(savedCases.map((item) => [normalizeCaseId(item), item]));
    const selectedCases = orderedCaseIds.map((caseId) => {
        const savedCase = savedCasesById.get(caseId);
        return savedCase ? { ...savedCase, id: caseId } : { id: caseId, label: `Case ${caseId}` };
    });

    const addCase = (result) => {
        if (!result || result.type !== 'case') return;

        const caseId = normalizeCaseId(result);
        if (!caseId) return;

        setSelectedCriteria((currentCriteria) => {
            const currentIds = Array.isArray(currentCriteria?.ordered_case_ids)
                ? currentCriteria.ordered_case_ids.map(normalizeCaseId)
                : [];
            if (currentIds.includes(caseId)) {
                return currentCriteria;
            }

            const currentSavedCases = Array.isArray(currentCriteria?.selected_cases)
                ? currentCriteria.selected_cases
                : [];
            const currentCasesById = new Map(currentSavedCases.map((item) => [normalizeCaseId(item), item]));
            const casesInOrder = currentIds.map((id) => currentCasesById.get(id) || { id, label: `Case ${id}` });
            casesInOrder.push(buildCaseSelection(result));

            return updateListCriteria(currentCriteria || {}, casesInOrder);
        });
    };

    const removeCase = (caseId) => {
        setSelectedCriteria((currentCriteria) => {
            const currentIds = Array.isArray(currentCriteria?.ordered_case_ids)
                ? currentCriteria.ordered_case_ids.map(normalizeCaseId)
                : [];
            const currentSavedCases = Array.isArray(currentCriteria?.selected_cases)
                ? currentCriteria.selected_cases
                : [];
            const currentCasesById = new Map(currentSavedCases.map((item) => [normalizeCaseId(item), item]));
            const remainingCases = currentIds
                .filter((id) => id !== caseId)
                .map((id) => currentCasesById.get(id) || { id, label: `Case ${id}` });

            return updateListCriteria(currentCriteria || {}, remainingCases);
        });
    };

    const clearCases = () => {
        setSelectedCriteria((currentCriteria) => updateListCriteria(currentCriteria || {}, []));
    };

    return (
        <div className='report-container list-report-builder'>
            <section className='list-report-search-panel'>
                <div className='list-report-panel-heading'>
                    <div>
                        <h3>Find Cases</h3>
                        <span className='subtext'>Search for a case and select it to add it to this report.</span>
                    </div>
                </div>
                <div className='list-report-case-search'>
                    <EnhancedSearch
                        expanded
                        setExpanded={() => {}}
                        modal
                        selectionMode
                        caseOnly
                        onChange={addCase}
                    />
                </div>
            </section>

            <section className='list-report-selection-panel'>
                <div className='list-report-panel-heading'>
                    <div>
                        <h3>Selected Cases</h3>
                        <span className='subtext'>Selection order is the default order for this report.</span>
                    </div>
                    <div className='list-report-selection-actions'>
                        <span className='list-report-count'>{selectedCases.length}</span>
                        {selectedCases.length > 0 && (
                            <button type='button' className='list-report-clear' onClick={clearCases} title='Clear selected cases'>
                                <Trash2 size={15} /> Clear
                            </button>
                        )}
                    </div>
                </div>

                {selectedCases.length === 0 ? (
                    <div className='list-report-empty'>
                        <ListOrdered size={28} />
                        <strong>No cases selected</strong>
                        <span className='subtext'>Cases will appear here in the order you select them.</span>
                    </div>
                ) : (
                    <div className='users-table-wrapper list-report-table-wrapper'>
                        <table className='users-table list-report-table'>
                            <thead className='small-text'>
                                <tr>
                                    <th>Case Name</th>
                                    <th>Case ID</th>
                                    <th>Phase</th>
                                    <th>Client</th>
                                    <th aria-label='Actions'></th>
                                </tr>
                            </thead>
                            <tbody>
                                {selectedCases.map((selectedCase, index) => {
                                    const caseId = normalizeCaseId(selectedCase);
                                    return (
                                        <tr key={caseId} className='subtext user-row list-report-case-row'>
                                            <td className='user-name-cell list-report-case-name-cell'>
                                                <span className='list-report-order-badge'>{index + 1}</span>
                                                <span>{selectedCase.label || `Case ${caseId}`}</span>
                                            </td>
                                            <td>{caseId}</td>
                                            <td>{selectedCase.phase || '—'}</td>
                                            <td>{selectedCase.contactName || '—'}</td>
                                            <td className='list-report-row-action-cell'>
                                                <button
                                                    type='button'
                                                    className='list-report-remove'
                                                    onClick={() => removeCase(caseId)}
                                                    aria-label={`Remove ${selectedCase.label || `case ${caseId}`}`}
                                                    title='Remove case'
                                                >
                                                    <X size={15} />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </div>
    );
};

export default ListReportCases;
